import * as React from 'react';

import { cn } from './lib/cn';

export interface InputProps extends React.ComponentProps<'input'> {
  /**
   * Show the browser's native ↑/↓ spin buttons on `type="number"` fields.
   * Defaults to `false` — number inputs render WITHOUT the spinner stepper
   * (keyboard ↑/↓ and typing are unaffected). Pass `spinner` to opt back into
   * the native arrows. No effect on non-number inputs.
   *
   * This controls the visible ARROWS only. Wheel-to-step is always blocked on
   * number inputs regardless of this prop — see the wheel guard below.
   */
  spinner?: boolean;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, spinner = false, ...props }, ref) => {
    const hideSpinner = type === 'number' && !spinner;

    // Our own handle on the node, merged with whatever ref the caller passed —
    // the wheel guard below can only be attached imperatively.
    const innerRef = React.useRef<HTMLInputElement | null>(null);
    const setRefs = React.useCallback(
      (node: HTMLInputElement | null) => {
        innerRef.current = node;
        if (typeof ref === 'function') ref(node);
        else if (ref) (ref as React.MutableRefObject<HTMLInputElement | null>).current = node;
      },
      [ref],
    );

    /**
     * Stop the mouse wheel from stepping a focused `type="number"` field.
     *
     * Hiding the spin buttons is purely cosmetic — the browser still steps the
     * value on wheel whenever the input has focus. So scrolling a form with the
     * pointer resting on an amount silently rewrites it: on a payroll salary or
     * a CRM quote line that is data corruption with no visible cause and no
     * undo. Keyboard ↑/↓ still steps, because that is a deliberate keystroke.
     *
     * Attached natively with `{ passive: false }` rather than through React's
     * `onWheel`, because React registers wheel at the root as PASSIVE, where
     * `preventDefault()` silently does nothing (and warns in console).
     *
     * `preventDefault()` — deliberately NOT the more common `blur()` trick.
     * Blurring would leave page scrolling intact, but several call sites commit
     * on blur (CRM's quote line items, invoice tab), so a stray scroll would
     * fire a real write. Guarding on focus keeps the cost narrow: an unfocused
     * number input never steps, so the page scrolls over it as normal.
     */
    React.useEffect(() => {
      if (type !== 'number') return;
      const el = innerRef.current;
      if (!el) return;
      const stopWheelStep = (event: WheelEvent) => {
        if (document.activeElement === el) event.preventDefault();
      };
      el.addEventListener('wheel', stopWheelStep, { passive: false });
      return () => el.removeEventListener('wheel', stopWheelStep);
    }, [type]);

    return (
      <input
        type={type}
        className={cn(
          // `text-lg` at EVERY breakpoint — one size for the typed value and,
          // by inheritance, for the placeholder, so a field does not change size
          // the moment the user types. Deliberately NO `placeholder:text-*` rule:
          // letting the placeholder inherit keeps the two locked together even
          // where a call site overrides the input's own size.
          //
          // The old `text-2xl md:text-xl` pair is gone on purpose. Because the
          // size is now unprefixed, a call-site `text-lg` actually takes effect —
          // under `md:text-xl` an unprefixed override lost above 768px (different
          // tailwind-merge groups), which silently pinned ~60 inputs to 14px.
          'border-input bg-background file:text-foreground placeholder:text-muted-foreground flex h-10 w-full rounded-md border px-3 py-2 text-lg file:border-0 file:bg-transparent file:text-xl file:font-medium focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-offset-0 focus-visible:ring-ring/40 focus-visible:border-ring/60 disabled:cursor-not-allowed disabled:opacity-50',
          hideSpinner &&
            '[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none',
          className,
        )}
        ref={setRefs}
        {...props}
      />
    );
  },
);
Input.displayName = 'Input';

export { Input };
